import React, { useMemo, useState } from "react"
import { Badge, Button, Col, Drawer, Menu, Row, Spin, Tag } from "antd"
import { Link } from "gatsby"
import logo from "../../static/assets/images/logo-orizzontale.svg"
import {
  AppstoreOutlined,
  BellFilled,
  CheckCircleOutlined,
  DesktopOutlined,
  HddOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  VideoCameraOutlined
} from "@ant-design/icons"
import {
  getUserDisplayName,
  isSubscriptionValid,
  translatePath
} from "../utils"
import { useAuth0 } from "@auth0/auth0-react"
import { ROLE_TRAINER, ROLE_VIP } from "../enums/UserRoles"
import { useTranslation } from "react-i18next"
import "./MainMenu.less"
import NotificationFeeds from "./NotificationFeeds"
import { useGlobalValue } from "../hooks/useGlobalValue"

const { SubMenu } = Menu

const wingColsConfig = {
  sm: 12,
  md: 20,
  lg: 3,
  xl: 3,
  xxl: 3
}

const wingColsConfigMenu = {
  sm: 12,
  md: 4,
  lg: 3,
  xl: 3,
  xxl: 3
}

const wingColsConfigLogo = {
  sm: 12,
  md: 24,
  lg: 4,
  xl: 4,
  xxl: 5
}

const wingColsConfigCenterCol = {
  sm: 12,
  md: 20,
  lg: 15,
  xl: 15,
  xxl: 14
}

const getUserRoleBadge = role => {
  const roles = {
    [ROLE_TRAINER]: "Trainer",
    [ROLE_VIP]: "VIP"
  }

  return roles[role] ?? null
}

const getUserRoleTag = role => {
  const text = getUserRoleBadge(role)
  return text && <Tag color="#f50">{text}</Tag>
}

export const MobileMenu = ({ user }) => {
  const [showDrawer, setShowDrawer] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [{ userSubscription }] = useGlobalValue()
  const isPremium = isSubscriptionValid(userSubscription)
  const { isLoading } = useAuth0()
  const { t } = useTranslation()

  const handleClose = () => {
    setShowDrawer(false)
  }

  const userMenu = useMemo(() => {
    if (isLoading) {
      return (
        <Menu.Item key="loading-user">
          <Spin spinning />
        </Menu.Item>
      )
    }

    if (!user) {
      return (
        <Menu.Item key="sign-out">
          <Link to={translatePath("/auth/sign-in")}>{t("menu:signIn")}</Link>
        </Menu.Item>
      )
    }

    return (
      <>
        <Menu.Item
          key="notification"
          icon={<BellFilled />}
          onClick={() => {
            setShowNotification(true)
          }}
        >
          <Link to={translatePath("/#")}>{t("menu:notifications")}</Link>
        </Menu.Item>
        <Menu.Item key="profile" icon={<UserOutlined />}>
          <Link to={translatePath("/me")}>
            {t("menu:profile")} {getUserRoleTag(user.role)}
          </Link>
        </Menu.Item>
        <Menu.Item key="sign-out" icon={<LogoutOutlined />}>
          <Link to={translatePath("/auth/sign-out")}>{t("menu:signOut")}</Link>
        </Menu.Item>
      </>
    )
  }, [isLoading, user])

  return (
    <div className="main-menu mobile">
      <Row justify="space-between">
        <Col>
          <Link to={"/"}>
            <img src={logo} alt="logo" style={{ maxHeight: 60 }} />
          </Link>
        </Col>
        <Col>
          <MenuOutlined
            onClick={() => {
              setShowDrawer(true)
            }}
          />
        </Col>
      </Row>
      <Drawer
        width={316}
        title={null}
        placement="right"
        closable={true}
        onClose={handleClose}
        visible={showDrawer}
      >
        <Menu mode="inline" theme={"dark"}>
          <Menu.Item key="search" icon={<SearchOutlined />}>
            <Link to={translatePath("/search")}>{t("menu:search")}</Link>
          </Menu.Item>
          <Menu.Item key="myVideos" icon={<DesktopOutlined />}>
            <Link to={translatePath("/me/library")}>{t("menu:library")}</Link>
          </Menu.Item>
          <Menu.Item key="home" icon={<HomeOutlined />}>
            <Link to={"/"}>{t("menu:subscription")}</Link>
            {user && (
              <Badge
                className="subscription-page"
                count={isPremium ? "Premium" : "Free"}
                style={isPremium ? { backgroundColor: "#ec9a0c" } : ""}
              />
            )}
          </Menu.Item>
          <SubMenu
            key="onDemand-submenu"
            title={<Link to={"/on-demand"}>{t("menu:onDemand")}</Link>}
            icon={<VideoCameraOutlined />}
            className={"on-demand-mobile"}
          >
            <Menu.Item key="courses">
              <Link to={"/on-demand/courses"}>{t("menu:courses")}</Link>
            </Menu.Item>
            <Menu.Item key="programs">
              <Link to={"/on-demand/programs"}>{t("menu:programs")}</Link>
            </Menu.Item>
            <Menu.Item key="master">
              <Link to={"/on-demand/masters"}>{t("menu:masters")}</Link>
            </Menu.Item>
          </SubMenu>
          <Menu.Item key="allCategories" icon={<AppstoreOutlined />}>
            <Link to={translatePath("/category/all")}>
              {t("menu:categories")}
            </Link>
          </Menu.Item>
          <Menu.Item key="allCertifications" icon={<CheckCircleOutlined />}>
            <Link to={translatePath("/certification/all")}>
              {t("menu:certifications")}
            </Link>
          </Menu.Item>
          <Menu.Item key="allChannels" icon={<HddOutlined />}>
            <Link to={translatePath("/channel/all")}>{t("menu:channels")}</Link>
          </Menu.Item>
          <Menu.Item key="allAuthors" icon={<TeamOutlined />}>
            <Link to={translatePath("/author/all")}>{t("menu:authors")}</Link>
          </Menu.Item>
          <Menu.Item key="webinar" className="highlighted">
            <Link to={"/webinar"}>{t("menu:webinar")}</Link>
          </Menu.Item>
          {userMenu}
        </Menu>
      </Drawer>

      {showNotification && (
        <Drawer
          height="100%"
          placement="bottom"
          title={t("label:notifications")}
          visible={true}
          onClose={() => {
            setShowNotification(false)
          }}
        >
          <NotificationFeeds />
        </Drawer>
      )}
    </div>
  )
}

export const DesktopMenu = ({ user }) => {
  const { user: auth0User, isLoading } = useAuth0()
  const { t } = useTranslation()
  const [showNotification, setShowNotification] = useState(false)

  const [{ userSubscription }] = useGlobalValue()
  const isPremium = isSubscriptionValid(userSubscription)

  const userMenu = useMemo(() => {
    if (isLoading) {
      return (
        <Menu.Item key="sign-out">
          <Spin spinning />
        </Menu.Item>
      )
    }

    if (!user) {
      return (
        <SubMenu
          key="user-submenu"
          icon={
            <Link to={translatePath("/auth/sign-in")}>
              <UserOutlined />
            </Link>
          }
        >
          <Menu.Item key="sign-out">
            <Link to={translatePath("/auth/sign-in")}>{t("menu:signIn")}</Link>
          </Menu.Item>
        </SubMenu>
      )
    }

    return (
      <SubMenu
        key="user-submenu"
        icon={
          <Badge count={getUserRoleBadge(user.role)}>
            <Link to={translatePath("/me")}>
              <UserOutlined />
            </Link>
          </Badge>
        }
      >
        <Menu.Item key="profile">
          <Link to={translatePath("/me")}>
            {getUserDisplayName({ ...auth0User, ...user })}{" "}
            {getUserRoleTag(user.role)}
          </Link>
        </Menu.Item>
        <Menu.Item key="sign-out">
          <Link to={translatePath("/auth/sign-out")}>{t("menu:signOut")}</Link>
        </Menu.Item>
      </SubMenu>
    )
  }, [user, auth0User, isLoading])

  return (
    <div className="main-menu desktop">
      <Row justify="space-between">
        <Col {...wingColsConfigLogo}>
          <Link to={"/"}>
            <img src={logo} alt="logo" style={{ maxHeight: 60 }} />
          </Link>
        </Col>
        <Col {...wingColsConfigCenterCol}>
          <Menu mode="horizontal" theme={"dark"}>
            <Menu.Item key="library">
              <Link to={translatePath("/me/library")}>
                {t("menu:myLibrary")}
              </Link>
            </Menu.Item>
            <Menu.Item key="subscription">
              <Link to={translatePath("/")}>{t("menu:subscription")}</Link>
              {user && (
                <Badge
                  className="subscription-page"
                  count={isPremium ? "Premium" : "Free"}
                  style={isPremium ? { backgroundColor: "#ec9a0c" } : ""}
                />
              )}
            </Menu.Item>

            <SubMenu
              key="onDemand-submenu"
              title={<Link to={"/on-demand"}>{t("menu:onDemand")}</Link>}
            >
              <Menu.Item key="courses">
                <Link to={"/on-demand/courses"}>{t("menu:courses")}</Link>
              </Menu.Item>
              <Menu.Item key="programs">
                <Link to={"/on-demand/programs"}>{t("menu:programs")}</Link>
              </Menu.Item>
              <Menu.Item key="master">
                <Link to={"/on-demand/masters"}>{t("menu:masters")}</Link>
              </Menu.Item>
            </SubMenu>

            <Menu.Item key="webinar" className="highlighted">
              <Link to={"/webinar"}>{t("menu:webinar")}</Link>
            </Menu.Item>
            <Menu.Item key="allCertifications">
              <Link to={translatePath("/certification/all")}>
                {t("menu:certifications")}
              </Link>
            </Menu.Item>
            <Menu.Item key="allCategories">
              <Link to={translatePath("/category/all")}>
                {t("menu:categories")}
              </Link>
            </Menu.Item>

            <Menu.Item key="allChannels">
              <Link to={translatePath("/channel/all")}>
                {t("menu:channels")}
              </Link>
            </Menu.Item>
            <Menu.Item key="allAuthors">
              <Link to={translatePath("/author/all")}>{t("menu:authors")}</Link>
            </Menu.Item>
          </Menu>
        </Col>
        <Col {...wingColsConfigMenu}>
          <Row justify="end">
            <Col>
              <Menu mode="horizontal" theme={"dark"}>
                <Menu.Item
                  key="notification"
                  onClick={() => {
                    setShowNotification(true)
                  }}
                >
                  <Badge dot>
                    <BellFilled />
                  </Badge>
                </Menu.Item>
                <Menu.Item key="search">
                  <Link to={translatePath("/search")}>
                    <SearchOutlined />
                  </Link>
                </Menu.Item>
                {userMenu}
              </Menu>
            </Col>
          </Row>
        </Col>
      </Row>
      {showNotification && (
        <Drawer
          width={500}
          title={t("label:notifications")}
          visible={true}
          onClose={() => {
            setShowNotification(false)
          }}
        >
          <NotificationFeeds />
        </Drawer>
      )}
    </div>
  )
}

export const LandingPageMenu = ({ user }) => {
  const { user: auth0User, isLoading, loginWithRedirect } = useAuth0()
  const { t } = useTranslation()

  const handleSignUp = async () => {
    await loginWithRedirect({
      screen_hint: "signUp",
      ui_language: (process.env.GATSBY_LANG || "it_IT").split("_")[0]
    })
  }

  const handleSignIn = async () => {
    await loginWithRedirect({
      ui_language: (process.env.GATSBY_LANG || "it_IT").split("_")[0]
    })
  }

  const userMenu = useMemo(() => {
    if (isLoading) {
      return <Spin spinning />
    }

    return (
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Button
            style={{ width: "100%" }}
            type="primary"
            className="white"
            onClick={handleSignIn}
          >
            {t("button:signInNow")}
          </Button>
        </Col>
        <Col span={12}>
          <Button
            style={{ width: "100%" }}
            type="primary"
            className="yellowsp"
            onClick={handleSignUp}
          >
            {t("button:signUp")}
          </Button>
        </Col>
      </Row>
    )
  }, [user, auth0User, isLoading])

  return (
    <Row
      justify="space-between"
      align="middle"
      gutter={[8, 8]}
      className="landing-menu"
    >
      <Col xs={12} sm={12} md={8} lg={6} xl={4}>
        <Link to={"/landing"}>
          <img src={logo} alt="logo" />
        </Link>
      </Col>
      <Col xs={10} sm={10} md={12} lg={8} xl={8} xxl={6}>
        <div>{userMenu}</div>
      </Col>
    </Row>
  )
}
